import React from 'react';
import {
  Box,
  Button,
  Flex,
  Stack,
  SvgX,
  CookbookProvider,
  grubhubDark,
} from '@grubhubprod/gh-cookbook-react';
import { PageSection } from 'src/components/selfService/layout/PageSection/PageSection';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { BalanceCreditApplicationButton } from 'src/components/selfService/common/Balance/components/BalanceCreditApplicationButton/BalanceCreditApplicationButton';
import { useBalanceContext } from 'src/components/selfService/common/Balance/hooks/useBalanceContext';
import { useLocalStorageDismiss } from 'src/components/selfService/common/Balance/hooks/useLocalStorageDismiss';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';

export function NetTermsPromotionSection(): JSX.Element {
  const { isPhone } = useScreenSizes();
  const balanceContext = useBalanceContext();
  const { isNetTermsPromotionDismissed, dismissNetTermsPromotion } =
    useLocalStorageDismiss();
  const { value: isBalancePostLaunchImprovementsEnabled } = useFeatureFlag(
    FeatureFlag.BALANCE_POST_LAUNCH_IMPROVEMENTS
  );

  if (
    balanceContext.isCreditInfoLoading ||
    balanceContext.isOnNetTerms ||
    isNetTermsPromotionDismissed ||
    !isBalancePostLaunchImprovementsEnabled
  ) {
    return null;
  }

  const DismissBunnerButton = (): JSX.Element => (
    <Button variant="tertiary" type="button" onClick={dismissNetTermsPromotion}>
      <Box color="primaryText" display="flex">
        <SvgX aria-label="close" size="24px" />
      </Box>
    </Button>
  );

  return (
    <Stack y="md" position="relative">
      {isPhone && (
        <Box position="absolute" top="6px" right="3px">
          <DismissBunnerButton />
        </Box>
      )}

      <PageSection
        dataTestId="net-terms-promotion-section"
        borderRadius="medium"
        bg="tintMint"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexDirection={isPhone ? 'column' : 'row'}
          style={{
            gap: isPhone ? '24px' : '64px',
          }}
        >
          <Box flex="1">
            <PageSectionTitle
              y="none"
              title="Enjoy financial flexibility with net terms"
              headingVariant="h3"
              subtitleY="xxx-sm"
              subtitle="Upgrade your payment method to net terms invoicing through Balance, so you can better manage your cash flow. Apply now to get started."
            />
          </Box>

          <Flex alignItems="center" width={isPhone ? '100%' : 'auto'}>
            <CookbookProvider theme={grubhubDark} useStyleResets={false}>
              <BalanceCreditApplicationButton
                text="Apply Now"
                block
                fullWidth={isPhone}
                variant="primary"
                flavor="inverted"
              />
            </CookbookProvider>
            {!isPhone && <DismissBunnerButton />}
          </Flex>
        </Flex>
      </PageSection>
    </Stack>
  );
}
